import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AcademicCapIcon,
  ClockIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid'
import { Card, Badge, InlineSpinner, EmptyState } from '@/components/ui'
import { lmsApi } from '@/api'
import type { Enrollment } from '@/api/lms'

export default function MyCoursesPage() {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchEnrollments()
  }, [])

  const fetchEnrollments = async () => {
    try {
      const data = await lmsApi.getMyEnrollments()
      setEnrollments(data)
    } catch (error) {
      console.error('Error fetching enrollments:', error)
    } finally {
      setLoading(false)
    }
  }

  const getLevelBadge = (level: string) => {
    const config: Record<string, { variant: 'success' | 'warning' | 'danger'; label: string }> = {
      basic: { variant: 'success', label: 'Base' },
      beginner: { variant: 'success', label: 'Base' },
      intermediate: { variant: 'warning', label: 'Intermedio' },
      advanced: { variant: 'danger', label: 'Avanzato' },
    }
    return config[level] || { variant: 'success' as const, label: level || 'N/D' }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento corsi..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">I miei corsi</h1>
          <p className="text-gray-600">I corsi a cui sei iscritto</p>
        </div>
        <Link
          to="/courses"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <AcademicCapIcon className="h-5 w-5 mr-2" />
          Esplora catalogo
        </Link>
      </div>

      {/* Enrollments Grid */}
      {enrollments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {enrollments.map((enrollment) => (
            <Link
              key={enrollment.id}
              to={`/courses/${enrollment.course.slug}`}
              className="group"
            >
              <Card className="h-full hover:shadow-lg transition-shadow">
                {/* Thumbnail */}
                <div className="relative h-40 -mx-4 -mt-4 mb-4 overflow-hidden rounded-t-lg bg-gray-100">
                  {(enrollment.course.thumbnail_url || enrollment.course.thumbnail) ? (
                    <img
                      src={enrollment.course.thumbnail_url || enrollment.course.thumbnail || ''}
                      alt={enrollment.course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
                      <AcademicCapIcon className="h-16 w-16 text-primary-400" />
                    </div>
                  )}
                  {enrollment.completed_at && (
                    <div className="absolute top-2 left-2">
                      <Badge variant="success">
                        <CheckCircleSolidIcon className="h-4 w-4 mr-1" />
                        Completato
                      </Badge>
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <Badge variant={getLevelBadge(enrollment.course.level).variant}>
                      {getLevelBadge(enrollment.course.level).label}
                    </Badge>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  {enrollment.course.category && (
                    <Badge size="sm">{enrollment.course.category.name}</Badge>
                  )}
                  <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {enrollment.course.title}
                  </h3>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {enrollment.course.duration_hours}h
                    </span>
                    <span className="flex items-center">
                      <BookOpenIcon className="h-4 w-4 mr-1" />
                      {enrollment.course.lessons_count} lezioni
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="pt-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progresso</span>
                      <span>{enrollment.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          enrollment.progress === 100 ? 'bg-green-500' : 'bg-primary-600'
                        }`}
                        style={{ width: `${enrollment.progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Enrolled date */}
                  <p className="text-xs text-gray-400">
                    Iscritto il {new Date(enrollment.enrolled_at).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<AcademicCapIcon className="h-12 w-12" />}
          title="Nessun corso"
          description="Non sei ancora iscritto a nessun corso"
          action={{
            label: 'Esplora corsi',
            onClick: () => window.location.href = '/courses',
          }}
        />
      )}
    </div>
  )
}
